import { useState } from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { prepareImageFiles, UNSUPPORTED_IMAGE_FORMAT_MESSAGE } from '@/lib/prepare-image-file';

interface FileUploadButtonProps {
  id: string;
  accept: string;
  onFiles: (files: File[]) => void;
  label: string;
  icon: LucideIcon;
  className?: string;
}

export const FileUploadButton = ({
  id,
  accept,
  onFiles,
  label,
  icon: Icon,
  className,
}: FileUploadButtonProps) => {
  const [preparing, setPreparing] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={`border-border text-foreground ${className || ''}`}
        disabled={preparing}
        onClick={() => document.getElementById(id)?.click()}
      >
        {preparing ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Icon className="items-center mr-2 h-4 w-4" />
        )}
        {preparing ? 'Preparing…' : label}
      </Button>
      <input
        id={id}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        disabled={preparing}
        onChange={(event) => {
          const raw = Array.from(event.target.files ?? []);
          event.target.value = '';
          if (raw.length === 0) return;
          setPreparing(true);
          void prepareImageFiles(raw)
            .then(onFiles)
            .catch((error) => {
              console.error(error);
              toast.error(
                error instanceof Error ? error.message : UNSUPPORTED_IMAGE_FORMAT_MESSAGE
              );
            })
            .finally(() => setPreparing(false));
        }}
      />
    </>
  );
};
FileUploadButton.displayName = 'FileUploadButton';
